import React from 'react';
import usePoses from '../../../hooks/usePoses';
import useBackgrounds from '../../../hooks/useBackgrounds';
import usePricelist from '../../../hooks/usePricelist';
import useMultiSeasonPoses from '../../../hooks/useMultiSeasonPoses';
import watermarkImage from './Watermark_v2.png';
import horizWatermarkImage from './Horiz_Grad_Watermark.png';
// eslint-disable-next-line no-unused-vars
import extrastyles from './AbsolutePosImgs.scss';
import { LAYOUTS, BG_SUBSTRINGS, selectLayout } from './packageLayouts';

// The customer-facing package-preview compositor. Given the student's poses and a
// package option, it shows "what this package looks like" by absolutely
// positioning the student's photos over backgrounds with a watermark.
//
// This used to be a single 7,500-line component where every package/option's
// layout was hand-coded. The layouts now live as data in packageLayouts.js
// (selectLayout + LAYOUTS); this file only does the runtime work that genuinely
// depends on the student's live data: resolving pose ROLES and background TAGS to
// real image URLs. See packageLayouts.test.js for the parity guarantee.
//
// Note: called as a plain function — AbsolutePosImgs(customer, packageId, option)
// — from PackageDisplay, so its hooks run within PackageDisplay's render.

const isHorizontal = (url) => url.includes('.jpg') || url.includes('-23.png');
const randomIndex = (arr) => (arr && arr.length ? Math.floor(Math.random() * arr.length) : 0);
const pick = (subset, fallback) => (
  subset.length ? subset[randomIndex(subset)].imageUrl : fallback[randomIndex(fallback)].imageUrl
);

const AbsolutePosImgs = (customer, packageId, option) => {
  const { currentStudent } = customer;
  const { data: pricelist } = usePricelist(packageId);
  // Multi-season poses when the pricelist spans seasons / shares poses, else this season's.
  const { data: poses } = pricelist?.id && (pricelist?.multipleSeasons?.length || pricelist?.displaySharedPoses)
    ? useMultiSeasonPoses(currentStudent.id, packageId)
    : usePoses(currentStudent.id, packageId);
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: springbackgrounds } = useBackgrounds(packageId);

  // Don't render until poses are available, or if every pose is a horizontal .jpg.
  if (poses && poses.length > 0 && poses.every((p) => p.imageUrl && p.imageUrl.includes('.jpg'))) return null;
  if (!poses || poses.length === 0) return null;

  const layout = LAYOUTS[selectLayout(packageId, option.name)];
  if (!layout) return null;

  // --- Resolve a background TAG to a URL from the school's background set ---
  // (scans backgrounds[].imageUrl for the tag's substring; falls back to the first).
  const resolveBackground = (tag) => {
    if (!tag) return null;
    const needle = BG_SUBSTRINGS[tag];
    const hit = (backgrounds || []).find((b) => b.imageUrl.includes(needle))
      || (springbackgrounds || []).find((b) => b.imageUrl.includes(needle));
    if (hit) return hit.imageUrl;
    return backgrounds && backgrounds[0] ? backgrounds[0].imageUrl : null;
  };

  // --- Resolve pose ROLES to URLs from the student's live poses ---
  const verticals = poses.filter((p) => !isHorizontal(p.imageUrl));
  const roles = {
    pose1: poses[0].imageUrl,
    pose2: pick(verticals, poses),
    pose3: pick(verticals, poses),
    pose4: pick(verticals, poses),
    poseHoriz: poses[0].imageUrl,
  };

  // poseHoriz: scan forward past spring/camera-marker poses to a formal/horizontal one.
  let pindex = 0;
  if (poses[0].imageUrl.toLowerCase().includes('-spr0')) {
    while (poses.length > pindex && !roles.poseHoriz.includes('51.png')) {
      roles.poseHoriz = poses[pindex].imageUrl;
      pindex += 1;
    }
  }
  if (pindex >= poses.length) pindex = poses.length - 1;
  if (['U0', 'J0', 'E0'].some((m) => poses[pindex].imageUrl.includes(m))) {
    while (poses.length > pindex && !roles.poseHoriz.includes('25.jpg')) {
      roles.poseHoriz = poses[pindex].imageUrl;
      pindex += 1;
    }
  }

  // Package 610 uses spring-aware pose variants.
  if (Number(packageId) === 610) {
    const nonSpring = poses.filter((p) => !isHorizontal(p.imageUrl) && !p.imageUrl.includes('-spr0'));
    const nonE0 = poses.filter((p) => !isHorizontal(p.imageUrl) && !p.imageUrl.includes('-e0'));
    roles.pose2 = pick(nonSpring, poses);
    roles.pose3 = pick(nonSpring, poses);
    roles.springpose1 = pick(nonE0, poses);
    roles.springpose2 = pick(nonE0, poses);
  }

  return (
    <div>
      <div className="image-container">
        {layout.map((slot, i) => {
          if (!slot) return null; // hidden slot
          const poseUrl = roles[slot.pose];
          if (!poseUrl) return null;
          const style = {
            position: 'absolute', top: slot.top, left: slot.left, width: slot.width, zIndex: 0,
          };
          const backgroundUrl = resolveBackground(slot.background);
          const watermark = poseUrl.includes('.jpg') ? horizWatermarkImage : watermarkImage;
          return (
            // Slots are a fixed positional array, so the index is a stable key.
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>
              {backgroundUrl && <img src={backgroundUrl} alt="" style={style} />}
              <img src={poseUrl} alt="" style={style} />
              <img src={watermark} alt="Watermark" style={style} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default AbsolutePosImgs;
